import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Phone, Mail, MapPin, CheckCircle2, Clock, 
  DollarSign, Sparkles, ArrowRight, Menu, X 
} from 'lucide-react';
import { mockServices, mockFeatures, mockTestimonials } from '../data/mock';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-slate-950/95 backdrop-blur-md shadow-lg shadow-slate-900/50' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_print-solutions-86/artifacts/gq1nd1or_print%20world%20logo.png" 
                alt="Print World Gurgaon"
                className="h-14 w-auto cursor-pointer"
                onClick={() => scrollToSection('home')}
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-slate-300 hover:text-cyan-400 transition-colors font-medium">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-cyan-400 transition-colors font-medium">
                Products
              </button>
              <button onClick={() => scrollToSection('features')} className="text-slate-300 hover:text-cyan-400 transition-colors font-medium">
                Features
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-cyan-400 transition-colors font-medium">
                Contact
              </button>
              <Button onClick={() => scrollToSection('contact')} className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
                Get Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-slate-300 hover:text-cyan-400 transition-colors"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900/98 backdrop-blur-md border-t border-slate-800">
            <div className="px-4 py-6 space-y-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_print-solutions-86/artifacts/gq1nd1or_print%20world%20logo.png" 
                alt="Print World Gurgaon"
                className="h-12 w-auto mb-4"
              />
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2">
                Products
              </button>
              <button onClick={() => scrollToSection('features')} className="block w-full text-left text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2">
                Features
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2">
                Contact
              </button>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
                Get Quote
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Running Marquee Strip */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-yellow-400 py-3 overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content">
            <span className="marquee-text">
              🌟 Gold Foil Visiting Cards &nbsp;&nbsp;•&nbsp;&nbsp; 
              ✨ Spot UV Visiting Cards &nbsp;&nbsp;•&nbsp;&nbsp; 
              💎 Special Velvet Business Cards &nbsp;&nbsp;•&nbsp;&nbsp; 
              📄 Letter Heads &nbsp;&nbsp;•&nbsp;&nbsp; 
              🏷️ Paper Stickers &nbsp;&nbsp;•&nbsp;&nbsp; 
              🎯 Vinyl Stickers &nbsp;&nbsp;•&nbsp;&nbsp; 
              ⭐ Custom Shape Stickers &nbsp;&nbsp;•&nbsp;&nbsp; 
              🪪 PVC Aadhaar Card / Pan Card / RC / License / Ayushman Card Printing &nbsp;&nbsp;•&nbsp;&nbsp; 
              📋 Tent Card &nbsp;&nbsp;•&nbsp;&nbsp; 
              📚 Coffee Table Book &nbsp;&nbsp;•&nbsp;&nbsp; 
              📑 Brochures &nbsp;&nbsp;•&nbsp;&nbsp; 
              🎪 Standee &nbsp;&nbsp;•&nbsp;&nbsp; 
              🖼️ Flex &nbsp;&nbsp;•&nbsp;&nbsp; 
              🎭 Backdrop &nbsp;&nbsp;•&nbsp;&nbsp; 
              🖨️ Large Format Prints &nbsp;&nbsp;•&nbsp;&nbsp; 
              ⚡ High Quality Prints &nbsp;&nbsp;•&nbsp;&nbsp;
            </span>
            <span className="marquee-text">
              🌟 Gold Foil Visiting Cards &nbsp;&nbsp;•&nbsp;&nbsp; 
              ✨ Spot UV Visiting Cards &nbsp;&nbsp;•&nbsp;&nbsp; 
              💎 Special Velvet Business Cards &nbsp;&nbsp;•&nbsp;&nbsp; 
              📄 Letter Heads &nbsp;&nbsp;•&nbsp;&nbsp; 
              🏷️ Paper Stickers &nbsp;&nbsp;•&nbsp;&nbsp; 
              🎯 Vinyl Stickers &nbsp;&nbsp;•&nbsp;&nbsp; 
              ⭐ Custom Shape Stickers &nbsp;&nbsp;•&nbsp;&nbsp; 
              🪪 PVC Aadhaar Card / Pan Card / RC / License / Ayushman Card Printing &nbsp;&nbsp;•&nbsp;&nbsp; 
              📋 Tent Card &nbsp;&nbsp;•&nbsp;&nbsp; 
              📚 Coffee Table Book &nbsp;&nbsp;•&nbsp;&nbsp; 
              📑 Brochures &nbsp;&nbsp;•&nbsp;&nbsp; 
              🎪 Standee &nbsp;&nbsp;•&nbsp;&nbsp; 
              🖼️ Flex &nbsp;&nbsp;•&nbsp;&nbsp; 
              🎭 Backdrop &nbsp;&nbsp;•&nbsp;&nbsp; 
              🖨️ Large Format Prints &nbsp;&nbsp;•&nbsp;&nbsp; 
              ⚡ High Quality Prints &nbsp;&nbsp;•&nbsp;&nbsp;
            </span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section id="home" className="relative pt-44 pb-24 overflow-hidden min-h-screen flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_print-solutions-86/artifacts/59dcq98e_%D0%97%D0%B0%D0%BC%D0%BE%D0%B2%D0%B8%D1%82%D0%B8%20%D0%BF%D0%BE%D0%BB%D1%96%D0%B3%D1%80%D0%B0%D1%84%D1%96%D1%8E%20-%20%D1%80%D0%BE%D0%B7%D1%80%D0%BE%D0%B1%D0%BA%D0%B0%20%D1%82%D0%B0%20%D0%B4%D1%80%D1%83%D0%BA%20%D0%BC%D0%BE%D0%B6%D0%BD%D0%B0%20%D0%BF%D0%B5%D1%80%D0%B5%D0%B9%D1%88%D0%BE%D0%B2%D1%88%D0%B8%20%D0%B7%D0%B0%20%D0%BF%D0%BE%D1%81%D0%B8%D0%BB%D0%B0%D0%BD%D0%BD%D1%8F%D0%BC%20%D1%96%D0%BD%D1%81%D1%82%D0%B0%D0%B3%D1%80%D0%B0%D0%BC%20%D1%83%20%D1%88%D0%B0%D0%BF%D1%86%D1%96%20%D0%BF%D1%80%D0%BE%D1%84%D1%96%D0%BB%D1%8E%F0%9F%93%B2_%20%D0%92%D1%96%D0%B7%D0%B8%D1%82%D0%BA%D0%B0%20%D0%B7%D0%BE%D0%BB%D0%BE%D1%82%D0%BE%2C%20%D0%B2%D1%96%D0%B7%D0%B8%D1%82%D0%BA%D0%B8%20%D1%81%D1%80%D1%96%D0%B1%D0%BB%D0%BE%2C%20%D0%B4%D0%B8%D0%B7%D0%B0%D0%B9%D0%BD%20%D0%B2%D1%96%D0%B7%D0%B8%D1%82%D0%BE%D0%BA%2C%20%D0%B1%D1%96%D1%80%D0%BA%D0%B8%2C%20%D0%BB%D0%B8%D1%81%D1%82%D1%96%D0%B2%D0%BA%D0%B0%20%D0%B7%D0%BE%D0%BB%D0%BE%D1%82%D0%BE%2C%20%D0%BF%D0%BE%D0%BB%D1%96%D0%B3%D1%80%D0%B0%D1%84%D1%96%D1%8F%2C%20%D0%BA%D0%B0%D1%80%D1%82%D0%BA%D0%B0%20%D0%BB%D0%BE%D1%8F%D0%BB%D1%8C%D0%BD%D0%BE%D1%81%D1%82%D1%96%2C%20%D0%B4%D1%80%D1%83%D0%BA%20%D0%B1%D1%96%D1%80%D0%BE%D0%BA%2C.jpg')`
          }}
        ></div>
        
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-950/60"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full backdrop-blur-sm mb-8">
              <span className="text-cyan-400 text-sm font-medium">Premium Printing Solutions</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              <span className="text-white">High-Quality</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Printing Solutions
              </span>
              <br />
              <span className="text-white">for Every Business</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed mb-10">
              Fast delivery • Premium quality • Affordable pricing
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => scrollToSection('contact')}
                size="lg" 
                className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold px-10 py-7 text-lg group shadow-lg shadow-cyan-500/50"
              >
                Get a Quote
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => scrollToSection('services')}
                size="lg" 
                variant="outline" 
                className="border-cyan-500/50 bg-slate-950/50 backdrop-blur-sm text-cyan-400 hover:bg-cyan-500/10 px-10 py-7 text-lg"
              >
                View Products
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockFeatures.map((feature, index) => (
              <Card key={index} className="bg-slate-900/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10 group">
                <CardContent className="p-6 text-center space-y-3">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-cyan-500/10 rounded-full group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Our Printing <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              From business cards to large format banners, we deliver exceptional quality
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockServices.map((service, index) => (
              <Card key={index} className="bg-slate-900/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 overflow-hidden group hover:shadow-xl hover:shadow-cyan-500/10">
                <div 
                  className="aspect-video overflow-hidden relative cursor-pointer"
                  onClick={() => setSelectedService(service)}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent z-10"></div>
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-cyan-500/90 text-slate-950 px-4 py-2 rounded-full font-semibold text-sm">
                      Click to view details
                    </div>
                  </div>
                </div>
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-400">{service.description}</p>
                  <Button 
                    onClick={() => scrollToSection('contact')}
                    variant="ghost" 
                    className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 p-0 h-auto font-semibold group/btn"
                  >
                    Get Quote 
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-cyan-500/20"></div>
        <div className="absolute inset-0 backdrop-blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Need Professional Printing?
          </h2>
          <p className="text-xl text-slate-300">
            Get an instant quote and bring your ideas to life with premium quality prints
          </p>
          <Button 
            onClick={() => scrollToSection('contact')}
            size="lg" 
            className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold px-12 py-7 text-xl"
          >
            Get Instant Quote
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Get in <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Touch</span>
            </h2>
            <p className="text-xl text-slate-400">
              Let's discuss your printing needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <Card className="bg-slate-900/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300">
                <CardContent className="p-8 space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Phone</p>
                      <a href="tel:7011063233" className="text-white text-lg font-semibold hover:text-cyan-400 transition-colors">
                        +91 7011063233
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Email</p>
                      <a href="mailto:orders@theprintworld.in" className="text-white text-lg font-semibold hover:text-cyan-400 transition-colors break-all">
                        orders@theprintworld.in
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Location</p>
                      <p className="text-white text-lg font-semibold">
                        Gurgaon, India
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Website</p>
                      <a href="https://www.theprintworld.in" target="_blank" rel="noopener noreferrer" className="text-white text-lg font-semibold hover:text-cyan-400 transition-colors">
                        www.theprintworld.in
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8">
                <form 
                  action="https://formspree.io/f/mpqjgkzw"
                  method="POST"
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Service Required
                    </label>
                    <select
                      name="service"
                      required
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select a service</option>
                      <option>Visiting Cards</option>
                      <option>Brochures & Catalogues</option>
                      <option>Banners & Flex</option>
                      <option>Custom Shape Stickers</option>
                      <option>Letterhead</option>
                      <option>Carry Bag</option>
                      <option>Custom Desktop Calendar</option>
                      <option>Custom Envelopes</option>
                      <option>Stamps</option>
                      <option>Flyers</option>
                      <option>Certificates</option>
                      <option>Booklets</option>
                      <option>ID Cards</option>
                      <option>Canvas Prints</option>
                      <option>LED Boards</option>
                      <option>Table Tent Card</option>
                      <option>Photos Printing</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      rows="4"
                      required
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your project..."
                    ></textarea>
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold py-6 text-lg"
                  >
                    Send Quote Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_print-solutions-86/artifacts/gq1nd1or_print%20world%20logo.png" 
                alt="Print World Gurgaon"
                className="h-16 w-auto mb-2"
              />
              <p className="text-slate-400 text-sm">
                Premium commercial printing solutions for businesses of all sizes.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('home')} className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                    Products
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2">
                <li className="text-slate-400 text-sm">Visiting Cards</li>
                <li className="text-slate-400 text-sm">Brochures</li>
                <li className="text-slate-400 text-sm">Banners & Flex</li>
                <li className="text-slate-400 text-sm">Custom Stickers</li>
                <li className="text-slate-400 text-sm">Carry Bags</li>
                <li className="text-slate-400 text-sm">Letterhead</li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-2 text-slate-400 text-sm">
                  <Phone size={16} className="text-cyan-400" />
                  <a href="tel:7011063233" className="hover:text-cyan-400 transition-colors">
                    +91 7011063233
                  </a>
                </li>
                <li className="flex items-center space-x-2 text-slate-400 text-sm">
                  <Mail size={16} className="text-cyan-400" />
                  <a href="mailto:orders@theprintworld.in" className="hover:text-cyan-400 transition-colors break-all">
                    orders@theprintworld.in
                  </a>
                </li>
                <li className="flex items-center space-x-2 text-slate-400 text-sm">
                  <Sparkles size={16} className="text-cyan-400" />
                  <a href="https://www.theprintworld.in" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
                    www.theprintworld.in
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-900 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} PRINT WORLD. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/917011063233?text=Hi%20Print%20World!%20I%20would%20like%20to%20get%20a%20quote%20for%20printing%20services."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/50 hover:scale-110 transition-all duration-300 z-50 group"
        aria-label="Chat on WhatsApp"
      >
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></span>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full"></span>
      </a>

      {/* Service Details Modal */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-4xl bg-slate-900 border-slate-800 text-white max-h-[90vh] overflow-y-auto">
          {selectedService && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  {selectedService.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Image */}
                <div className="rounded-lg overflow-hidden border border-slate-800">
                  <img 
                    src={selectedService.image} 
                    alt={selectedService.title}
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-2">Description</h3>
                  <p className="text-slate-300 leading-relaxed">{selectedService.description}</p>
                </div>

                {/* Specifications */}
                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-4">Specifications</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {selectedService.specifications?.map((spec, index) => (
                      <div key={index} className="bg-slate-950/50 p-4 rounded-lg border border-slate-800">
                        <div className="flex items-start space-x-3">
                          <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-white">{spec.label}</p>
                            <p className="text-slate-400 text-sm">{spec.value}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <div className="flex gap-4 pt-4">
                  <Button 
                    onClick={() => {
                      setSelectedService(null);
                      scrollToSection('contact');
                    }}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold py-6 text-lg"
                  >
                    Get Quote for {selectedService.title}
                  </Button>
                  <Button 
                    onClick={() => setSelectedService(null)}
                    variant="outline"
                    className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
